import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Paper,
} from "@mui/material";
import { Add, Edit, Assessment } from "@mui/icons-material";
import { useBillingCompanyContext } from "../../../../providers";
import { IPaymentReminderConfigForm } from "../types/paymentReminderConfig.types";
import { PaymentReminderConfigDialog } from "../dialogs/PaymentReminderConfigDialog";
import { DataTableV2 } from "../../../../components/TableV2/DataTableV2";
import {
  useTableV2State,
} from "../../../../components/TableV2/hooks/useTableV2State";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../components/TableV2/interfaces/IDataTableV2Props";
import { StandardTableActions } from "../../../../components/StandardTableActions/StandardTableActions";
import { useTableData } from "../../../../hooks/useTableData";
import { TABLE_CACHING_KEYS, STANDARD_APP_DATE_FORMAT } from "../../../../constants/constants";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";
import moment from "moment";

/**
 * Tab 1: Reminders — Configuration List
 * Uses DataTableV2 to show all automation configurations for the current outlet.
 */
export const ReminderConfigList: React.FC = () => {
  const { comapny } = useBillingCompanyContext();
  const { ftDateTime } = useTimezoneFormatter();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] =
    useState<IPaymentReminderConfigForm | null>(null);

  const {
    state,
    onPageChange,
    onRowsPerPageChange,
    onResizeMasterColumns,
    setColumnVisibility,
    initialColumnVisibility,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "allTimes",
      selectedTab: "-1",
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 25,
      apiFetchRowCount: 26,
      extraFetchFactor: 1,
    },
    cachingFilters: {
      uniqueKey: TABLE_CACHING_KEYS.PAYMENT_REMINDER_CAMPAIGN_LIST + "_CONFIG",
    },
  });

  const { columnsConfig, pagination, masterColumnResize } = state;

  const { data: list, loading, totalRecords, refetch } = useTableData<IPaymentReminderConfigForm>({
    path: "/campaign/get-whatsapp-campaign-automation-configurations",
    params: {
      company_outlet_uuid: comapny.company_outlet_uuid,
    },
  });

  const handleCreate = () => {
    setEditingConfig(null);
    setDialogOpen(true);
  };

  const handleEdit = (config: IPaymentReminderConfigForm) => {
    setEditingConfig(config);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingConfig(null);
  };

  const handleSuccess = () => {
    handleDialogClose();
    refetch();
  };

  const tableProps: IDataTableV2Props = {
    mobileColumns: [],
    isPagination: false,
    rowsPerPageOptions: pagination.rowsInPerPage,
    isDataLoading: loading,
    extraFetchFactor: pagination.extraFetchFactor,
    totalRecords: totalRecords,
    pageNumber: pagination.pageNumber,
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          initialColumnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: { onClick: () => refetch() },
      },
      leftItems: { customPlugins: [] },
      rightItems: {
        customPlugins: [
          {
            key: "create",
            onRender: () => (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreate}
                size="small"
              >
                Create Reminder
              </Button>
            ),
          },
        ],
      },
    },
    masterColumns: [
      {
        key: "actions",
        headerName: "Actions",
        fieldName: "",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        isFirstColumnSticky: true,
        minWidth: 80,
        onRowCellRender: (value, row: IPaymentReminderConfigForm) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <StandardTableActions
              status={row.status}
              onEditClick={() => handleEdit(row)}
              // onAnalyticsClick={() => {
              //   setOpenAnalytics(row as IMarketCampaignBasicInfo);
              // }}
            />
          </Box>
        ),
      },
      {
        key: "configuration_name",
        headerName: "Configuration Name",
        fieldName: "configuration_name",
        renderType: DataTableV2RowRenderType.TEXT,
        enableSorting: true,
      },
      {
        key: "configuration_description",
        headerName: "Description",
        fieldName: "configuration_description",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "template_name",
        headerName: "Template",
        fieldName: "template_name",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IPaymentReminderConfigForm) => (
          <Typography
            variant="body2"
            color={row.status === "ACTIVE" ? "success.main" : "text.secondary"}
            fontWeight={600}
          >
            {row.status || "—"}
          </Typography>
        ),
      },
      {
        key: "create_ts",
        headerName: "Created At",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME,
        enableSorting: true,
        onRowCellRender: (value, row: any) =>
          row.create_ts ? ftDateTime(row.create_ts) : "—",
      },
    ],
    rows: list,
    onPageChange,
    onRowsPerPageChange,
    masterColumnResize: {
      values: masterColumnResize,
      onResize: onResizeMasterColumns,
    },
  };

  return (
    <Box>
      <DataTableV2 {...tableProps} />

      <PaymentReminderConfigDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        initialData={editingConfig}
        onSuccess={handleSuccess}
      />
    </Box>
  );
};
